import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useStore } from "zustand";
import { useParams, useNavigate } from "react-router";
import { useMutationState } from "@tanstack/react-query";

import {
  Content,
  Text,
  Footer,
  DialogTrigger,
  UserCard,
  Avatar,
  ActionButtonGroup,
  ActionButton,
  TextField,
  Skeleton,
  TableView,
  TableHeader,
  TableBody,
  Column,
  Row,
  Cell,
  Image,
  SegmentedControl,
  SegmentedControlItem,
} from "@react-spectrum/s2";

import { store } from "../../store";
import { getStoriesKey, useStory, useUpdateStory } from "@app/hooks/stories";
import { useBreadcrumbs } from "@app/components/Breadcrumbs";

import { StoryCard } from "./StoryCard";
import { StoryToolbar } from "./StoryToolbar";
import { PersonaSelectDialog } from "./PersonaSelectDialog";
import { StoryImage } from "./StoryImage";

import EditIcon from "@react-spectrum/s2/icons/Edit";
import BrowseIcon from "@react-spectrum/s2/icons/Binoculars";
import CheckmarkIcon from "@react-spectrum/s2/icons/Checkmark";
import CloseIcon from "@react-spectrum/s2/icons/Close";
import TableIcon from "@react-spectrum/s2/icons/Table";
import ViewGridIcon from "@react-spectrum/s2/icons/ViewGrid";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };
import { PersonaAvatar } from "@app/components/PersonaAvatar";

const CONTAINER_WIDTH = 800;
const CONTAINER_PADDING = 24;

// Helper component to render table visual cell with hook
const VisualCell = ({ visuals }) => {
  return (
    <StoryImage
      visuals={visuals}
      styles={style({
        aspectRatio: "16 / 10",
      })}
    />
  );
};

VisualCell.propTypes = {
  visual: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  alt: PropTypes.string,
};

const storyCardStyles = style({
  height: "auto",
  flexShrink: 0,
  marginStart: {
    default: 0,
    isFirst: `calc(50vw - min(${CONTAINER_WIDTH / 2}px, (50vw - ${CONTAINER_PADDING * 2}px)) - ${CONTAINER_PADDING * 2}px)`,
  },
  marginEnd: {
    default: 0,
    isLast: `calc(50vw - min(${CONTAINER_WIDTH / 2}px, (50vw - ${CONTAINER_PADDING * 2}px)) - ${CONTAINER_PADDING * 2}px)`,
  },
});

export const Story = () => {
  const params = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!params?.id) {
      // TODO: Add 404 page
      navigate("/stories");
    }
  }, [navigate, params?.id]);

  const storyQuery = useStory(params.id, {
    enabled: params?.id !== "generate",
  });
  const story = storyQuery.data;
  const updateStory = useUpdateStory(params.id);

  const storyGenerationStatus = useMutationState({
    filters: { mutationKey: getStoriesKey("generate") },
    select: (mutation) => mutation.state.status,
  });

  const isGenerating =
    params.id === "generate" ||
    storyGenerationStatus[storyGenerationStatus.length - 1] === "pending";

  if (storyQuery.isError) {
    // TODO: Show error state
    console.error(storyQuery.error);
    navigate("/stories");
  }

  useEffect(() => {
    if (!storyQuery.isPending && !story) {
      debugger;
      // TODO: Try to fetch the story again and show a loading state
      // If still not found, redirect to 404
      console.warn(`Story with id ${params.id} not found`);
      // TODO: Add 404 page
      navigate("/stories");
    } else if (story?.title) {
      // If story is found, set the title
      document.title = `BeStory | ${story?.title}`;
    }
  }, [story, params.id, navigate]);

  const isLoading =
    !storyQuery || (!story && !!storyQuery.isFetching) || isGenerating;

  useBreadcrumbs([
    { label: "Stories", href: "/stories" },
    {
      label: story?.title ?? "Story",
      href: `/stories/${params.id}`,
      isLoading: isLoading || !story?.title,
    },
  ]);

  const personas = useStore(store, (state) => state.personas);

  const isPersonasLoaded = useStore(store, (state) => state.isPersonasLoaded);

  const generateStoryImages = useStore(
    store,
    (state) => state.generateStoryImages,
  );

  // Load personas when component mounts
  // useEffect(() => {
  //   if (!isPersonasLoaded) {
  //     console.log("Loading personas...");
  //     importAllPersonas();
  //   }
  // }, [importAllPersonas, isPersonasLoaded]);

  useEffect(() => {
    console.log("isPersonasLoaded:", isPersonasLoaded);
  }, [personas, isPersonasLoaded]);

  const sections = useMemo(() => {
    if (!story?.script) {
      return [];
    }
    return story.script.reduce((acc, item, idx) => {
      const itemWithIndex = { ...item, index: idx + 1 };
      if (item.section !== story.script[idx - 1]?.section) {
        acc.push([itemWithIndex]);
      } else {
        acc[acc.length - 1].push(itemWithIndex);
      }

      return acc;
    }, []);
  }, [story?.script]);

  const [scriptView, setScriptView] = useState("cards");

  const tableColumns = [
    { name: "Idx", id: "idx", isRowHeader: true, width: 50, align: "end" },
    { name: "Visual", id: "visual", width: 180 },
    { name: "Section", id: "section", width: 180 },
    { name: "Title", id: "title", width: 320 },
    { name: "Paragraph Text", id: "text" },
    { name: "Personas", id: "personas", width: 180 },
  ];

  const [isTitleEditable, setTitleEditable] = useState(false);
  const [title, setTitle] = useState(story?.title || "");

  useEffect(() => {
    setTitle(story?.title || "");
  }, [story?.title]);

  const handleTitleUpdate = (newTitle) => {
    if (newTitle.trim() !== story.title) {
      updateStory.mutate({
        title: newTitle.trim(),
      });
    }
    setTitleEditable(false);
    setTitle(story?.title || "");
  };

  const handlePersonaUpdate = (persona, selectedImage) => {
    updateStory.mutate({
      personas: story.personas.map((p) => {
        return p.id === persona.id
          ? {
              ...p,
              libraryId: selectedImage.id,
              avatarId: selectedImage.avatarId,
            }
          : p;
      }),
    });
  };

  return (
    <>
      {isLoading && (
        <div
          className={style({
            width: 360,
            height: 56,
            flexGrow: 0,
            flexShrink: 0,
          })}
        />
      )}
      {!isLoading && <StoryToolbar storyId={story?.id} />}
      <div
        className={style({
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: {
            default: "start",
            isGenerating: "center",
          },
          // minHeight: "[100%]",
          maxWidth: "[100%]",
          width: "100%",
          paddingX: CONTAINER_PADDING,
          boxSizing: "border-box",
          // overflow: "auto",
          // position: "relative",
        })({ isGenerating })}
      >
        <Skeleton isLoading={isLoading}>
          <div
            className={style({
              width: CONTAINER_WIDTH,
              maxWidth: "100%",
              marginTop: {
                default: { default: 32, lg: 0 },
                isGenerating: 80,
              },
            })({ isGenerating })}
          >
            <div
              className={style({
                display: "flex",
                alignItems: "center",
                gap: 8,
                font: "detail",
                marginBottom: 8,
              })}
            >
              <Text>Title</Text>
              <ActionButtonGroup density="compact" size="XS">
                {!isTitleEditable && (
                  <ActionButton
                    isQuiet
                    aria-label="Edit title"
                    onPress={() => setTitleEditable(true)}
                  >
                    <EditIcon />
                  </ActionButton>
                )}
                {isTitleEditable && (
                  <>
                    <ActionButton
                      aria-label="Save title"
                      onPress={() => handleTitleUpdate(title)}
                    >
                      <CheckmarkIcon />
                    </ActionButton>
                    <ActionButton
                      aria-label="Cancel title edit"
                      onPress={() => {
                        setTitleEditable(false);
                        setTitle(story?.title || "");
                      }}
                    >
                      <CloseIcon />
                    </ActionButton>
                  </>
                )}
              </ActionButtonGroup>
            </div>
            <div
              className={style({
                font: "heading",
                marginBottom: 24,
                whiteSpace: "pre-line",
              })}
            >
              {!isTitleEditable && <Text>{story?.title || "–"}</Text>}
              {isTitleEditable && (
                <TextField
                  autoFocus
                  value={title}
                  size="L"
                  isQuiet
                  onChange={setTitle}
                />
              )}
            </div>

            <div className={style({ font: "detail", marginBottom: 8 })}>
              <Text>Story overview</Text>
            </div>
            <div
              className={style({
                font: "body",
                marginBottom: 24,
                whiteSpace: "pre-line",
              })}
            >
              <Text>{story?.storyOverview || "–"}</Text>
            </div>

            <div className={style({ font: "detail", marginBottom: 8 })}>
              <Text>Customer</Text>
            </div>
            <div className={style({ font: "body", marginBottom: 24 })}>
              <Text>{story?.customer}</Text>
              <div className={style({ font: "body-xs", display: "inline" })}>
                {" "}
                <Text>({story?.industry || "–"})</Text>
              </div>
            </div>

            <div className={style({ font: "detail", marginBottom: 8 })}>
              <Text>Business goals</Text>
            </div>
            <div
              className={style({
                font: "body",
                marginBottom: 24,
                whiteSpace: "pre-line",
              })}
            >
              <Text>{story?.businessGoals || "–"}</Text>
            </div>

            <div className={style({ font: "detail", marginBottom: 8 })}>
              <Text>Campaign focus</Text>
            </div>
            <div
              className={style({
                font: "body",
                marginBottom: 24,
                whiteSpace: "pre-line",
              })}
            >
              <Text>{story?.businessGoals || "–"}</Text>
            </div>

            {/* <div className={style({ font: "detail", marginBottom: 8 })}>
                <Text>Products</Text>
              </div>
              <div className={style({ font: "body", marginBottom: 24 })}>
                {!!story.data?.products?.length ? (
                  <TagGroup selectionMode="none" aria-label="Products">
                    {story.data?.products?.map((product, idx) => (
                      <Tag key={`${product}-${idx}`}>{product}</Tag>
                    ))}
                  </TagGroup>
                ) : (
                  "–"
                )}
              </div> */}
          </div>

          <div
            className={style({
              font: "detail",
              marginBottom: 8,
              width: CONTAINER_WIDTH,
              maxWidth: "100%",
              marginX: "auto",
            })}
          >
            <Text>Personas</Text>
          </div>
          <div
            className={style({
              width: "100%",
              minHeight: "min",
              display: "flex",
              gap: 24,
              alignItems: "start",
              flexWrap: "nowrap",
              overflowX: "auto",
              marginBottom: 24,
              paddingY: 8,
            })}
          >
            {isLoading && (
              <UserCard
                styles={storyCardStyles({ isFirst: true, isLast: true })}
              >
                <Avatar src={null} />
                <Content>
                  <div className={style({ display: "flex", gap: 8 })}>
                    <Text slot="title"></Text>
                  </div>
                  <div
                    className={style({
                      font: "ui-sm",
                      whiteSpace: "pre-line",
                    })}
                  >
                    <Text></Text>
                  </div>
                </Content>
                <Footer>
                  <div
                    className={style({
                      display: "flex",
                      flexDirection: "column",
                    })}
                  >
                    <Text slot="description">
                      <Text slot="description">Role: </Text>
                    </Text>
                    <Text slot="description">Age:</Text>
                    <Text slot="description">Location:</Text>
                  </div>
                </Footer>
              </UserCard>
            )}
            {story?.personas?.map((persona, idx) => (
              <UserCard
                key={`${persona.name}-${idx}`}
                variant="primary"
                styles={storyCardStyles({
                  isFirst: idx === 0,
                  isLast: idx === story.personas.length - 1,
                })}
              >
                <div>
                  <PersonaAvatar persona={persona} />
                  <DialogTrigger>
                    <ActionButton
                      isQuiet
                      aria-label="Change personas"
                      size="XS"
                      // onPress={() => handlePersonaSelection(persona.libraryId)}
                    >
                      <BrowseIcon />
                    </ActionButton>
                    <PersonaSelectDialog
                      defaultSelectedKeys={[persona.libraryId]}
                      onPrimaryAction={(selectedImage) =>
                        handlePersonaUpdate(persona, selectedImage)
                      }
                    />
                  </DialogTrigger>
                </div>
                <Content>
                  <div className={style({ display: "flex", gap: 8 })}>
                    <Text slot="title">{persona.name}</Text>
                  </div>
                  <div
                    className={style({
                      font: "ui-sm",
                      whiteSpace: "pre-line",
                    })}
                  >
                    <Text>{persona.description}</Text>
                  </div>
                </Content>
                <Footer>
                  <div
                    className={style({
                      display: "flex",
                      flexDirection: "column",
                    })}
                  >
                    <Text slot="description">
                      <Text slot="description">Role: {persona.role}</Text>
                    </Text>
                    <Text slot="description">
                      Age: {persona.age ? `${persona.age} yo` : "–"}
                    </Text>
                    <Text slot="description">
                      Location: {persona.location || "–"}
                    </Text>
                  </div>
                </Footer>
              </UserCard>
            ))}
          </div>

          <div
            className={style({
              font: "detail",
              marginBottom: 8,
              width: CONTAINER_WIDTH,
              maxWidth: "100%",
              marginX: "auto",
              display: "flex",
              gap: 16,
              alignItems: "center",
            })}
          >
            <Text>Script</Text>
            <SegmentedControl
              selectedKey={scriptView}
              onSelectionChange={setScriptView}
            >
              <SegmentedControlItem id="cards" aria-label="Cards View">
                <ViewGridIcon />
              </SegmentedControlItem>
              <SegmentedControlItem id="table" aria-label="Table View">
                <TableIcon />
              </SegmentedControlItem>
            </SegmentedControl>
          </div>

          {scriptView === "table" && (
            <div
              className={style({
                width: "100%",
                maxWidth: 1600,
              })}
            >
              <TableView density="spacious" overflowMode="wrap">
                <TableHeader>
                  {tableColumns.map((column) => (
                    <Column key={column.id} {...column}>
                      {column.name}
                    </Column>
                  ))}
                </TableHeader>
                <TableBody>
                  {story.script.map((item, idx) => (
                    <Row key={item.id}>
                      {tableColumns.map((column) => {
                        switch (column.id) {
                          case "idx":
                            return (
                              <Cell key={column.id} align="end">
                                {idx}
                              </Cell>
                            );
                          case "visual":
                            return (
                              <Cell key={column.id}>
                                <VisualCell visuals={item?.visuals} />
                              </Cell>
                            );
                          case "personas":
                            return (
                              <Cell key={column.id}>
                                <div
                                  className={style({
                                    display: "flex",
                                    gap: 8,
                                    flexDirection: "column",
                                  })}
                                >
                                  {item?.personas?.map((personaIdx) => (
                                    <div
                                      key={personaIdx}
                                      className={style({
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                      })}
                                    >
                                      <PersonaAvatar
                                        persona={story?.personas?.[personaIdx]}
                                      />
                                      <Text>
                                        {story.personas[personaIdx].name}
                                      </Text>
                                    </div>
                                  ))}
                                </div>
                              </Cell>
                            );
                          default:
                            return (
                              <Cell key={column.id}>
                                <div className={style({ paddingY: 8 })}>
                                  {item?.[column.id]}
                                </div>
                              </Cell>
                            );
                        }
                      })}
                    </Row>
                  ))}
                </TableBody>
              </TableView>
            </div>
          )}

          {scriptView === "cards" && (
            <div
              className={style({
                width: "100%",
                display: "flex",
                gap: 24,
                flexDirection: "column",
                alignItems: "start",
              })}
            >
              {isLoading && (
                <div
                  className={style({
                    width: "100%",
                    display: "flex",
                    gap: 24,
                    alignItems: "start",
                    flexWrap: "nowrap",
                    overflowX: "auto",
                    paddingY: 8,
                  })}
                >
                  {new Array(3).fill(0).map((_, idx, arr) => (
                    <StoryCard
                      key={idx}
                      item={{ index: idx }}
                      styles={storyCardStyles({
                        isFirst: idx === 0,
                        isLast: idx === arr.length - 1,
                      })}
                    />
                  ))}
                </div>
              )}
              {sections.map((section, idx) => (
                <div
                  key={idx}
                  className={style({
                    width: "100%",
                    display: "flex",
                    gap: 24,
                    alignItems: "start",
                    flexWrap: "nowrap",
                    overflowX: "auto",
                    paddingY: 8,
                  })}
                >
                  {section.map((item, sdx) => (
                    <StoryCard
                      key={sdx}
                      item={item}
                      storyId={story.id}
                      styles={storyCardStyles({
                        isFirst: sdx === 0,
                        isLast: sdx === section.length - 1,
                      })}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
        </Skeleton>
      </div>
    </>
  );
};
